import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getSlackUserLink,
  unlinkSlackUser,
  SlackUserLinkResponse,
} from "@/services/apis/slackApi";
import { captureCustomSentryException } from "@/utils/sentryHelpers";

/**
 * Prefix shared by every person's link entry. Nested under the same
 * `["slack", <appKey>, …]` namespace `useSlack` uses so a future second Slack app
 * gets its own entry rather than overwriting this one. Invalidate by this prefix
 * when the change is not about one person (a workspace disconnect).
 */
export const slackUserLinkQueryKeyPrefix = ["slack", "iq", "user-link"] as const;

/**
 * Cache key for ONE signed-in person's Slack link. The user id is part of the
 * key (as in `memoryQueryKeys`): the GET answers for whoever holds the session,
 * so an unscoped key would hand the next person on the same tab the previous
 * person's Slack name and email for as long as the entry lives.
 */
export const slackUserLinkQueryKey = (userId: string | number | undefined) =>
  [...slackUserLinkQueryKeyPrefix, userId] as const;

/**
 * The signed-in person's own Slack identity — read state, and unlink it.
 * `userId` is the signed-in user's id (`useAuth().userDetails?.userId`); the read
 * waits until it is known.
 *
 * There is deliberately no connect/link mutation: linking only ever starts in
 * Slack (the bot sends a sign-in button), so the website reads and clears.
 *
 * Query options mirror `useSlack`: this row mounts alongside the workspace card,
 * and an outage would otherwise have every mounting consumer refire the request.
 * `staleTime` dedupes the success path; `retryOnMount: false` is what stops an
 * errored query refiring when a later consumer mounts.
 *
 * The one deliberate difference is `refetchOnWindowFocus: "always"`: linking
 * happens in another window (Slack), so the row must show the new state the
 * moment the person comes back. `"always"` ignores `staleTime`, and with
 * `retry: false` an outage costs one request per focus, no more.
 */
export function useSlackUserLink(userId: string | number | undefined) {
  const queryClient = useQueryClient();
  const queryKey = slackUserLinkQueryKey(userId);
  const hasUser = userId != null;

  const {
    data,
    error,
    isLoading,
    isError,
    refetch,
  } = useQuery<SlackUserLinkResponse>({
    queryKey,
    queryFn: getSlackUserLink,
    enabled: hasUser,
    retry: false,
    refetchOnWindowFocus: "always",
    staleTime: 60_000,
    retryOnMount: false,
  });

  // The row tells the person; this tells us. A User Service outage shows up
  // here first, and the row alone would leave it invisible to the team.
  useEffect(() => {
    if (error) {
      captureCustomSentryException(error, undefined, { feature: "slack_user_link_fetch" });
    }
  }, [error]);

  const unlinkMutation = useMutation({
    mutationFn: unlinkSlackUser,
    // Write the cleared state straight into the cache instead of invalidating:
    // the backend has already unlinked every row, so a refetch would only spend
    // a round trip to learn what we know — and would flash the loading skeleton.
    // Cancel in-flight reads first: a focus refetch can land after the DELETE
    // and would write `linked: true` back over the cleared state.
    onSuccess: async () => {
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, (old?: SlackUserLinkResponse) => ({
        workspace_connected: old?.workspace_connected ?? false,
        linked: false,
        links: [],
      }));
    },
  });

  return {
    links: data?.links ?? [],
    isLinked: data?.linked ?? false,
    isWorkspaceConnected: data?.workspace_connected ?? false,
    // Until the user id is known the read is parked, not finished: show the
    // skeleton rather than a "not linked" verdict nobody has fetched.
    isLoading: isLoading || !hasUser,
    // A failed background refetch (window focus) keeps the last good data, so
    // the row keeps showing it instead of "Could not load" beside an Unlink
    // button. The error state is only for "never loaded". A Retry from there
    // needs no extra code: with no data, TanStack moves the query back to
    // pending, so `isLoading` turns true by itself.
    isError: isError && !data,
    refetch,
    unlink: () => unlinkMutation.mutateAsync(),
    isUnlinking: unlinkMutation.isPending,
  };
}
